/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetIdentityCheckRow, GetAuthProvidersRow } from '../openapi-types.gen.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetIdentityCheck ────────────────────────────────────────────────────────────

export interface useGetIdentityCheckArgs { readonly kind?: string; readonly email?: string; readonly provider?: string; readonly providerUserId?: string; readonly handle?: string; readonly fields?: readonly ('kind' | 'linkedProvider' | 'signupMethod' | 'connectedProviders' | 'provider' | 'username' | 'displayName' | 'avatarUrl' | 'email' | 'available')[]; }


async function _useGetIdentityCheckQueryFn(
  args: useGetIdentityCheckArgs,
  ctx: QueryFunctionContext,
): Promise<GetIdentityCheckRow | null> {
  const url = '/v1/auth/identity-check';
  const params = new URLSearchParams();
  if (args.fields !== undefined) params.set('fields', args.fields.join(','));
  if (args.kind !== undefined) params.set('kind', String(args.kind));
  if (args.email !== undefined) params.set('email', String(args.email));
  if (args.provider !== undefined) params.set('provider', String(args.provider));
  if (args.providerUserId !== undefined) params.set('providerUserId', String(args.providerUserId));
  if (args.handle !== undefined) params.set('handle', String(args.handle));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetIdentityCheck',
    routeTemplate: '/v1/auth/identity-check',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetIdentityCheckRow;
}


const _useGetIdentityCheckFactory = Object.assign(
  (args: useGetIdentityCheckArgs) => ({
    queryKey: ['auth', 'detail', { kind: args.kind ?? null, email: args.email ?? null, provider: args.provider ?? null, providerUserId: args.providerUserId ?? null, handle: args.handle ?? null }, args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetIdentityCheckQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetIdentityCheck',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['auth', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetIdentityCheckArgs, GetIdentityCheckRow>;


export const useGetIdentityCheckSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/auth/identity-check',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetIdentityCheckFactory },
  parseInput: (raw: unknown): useGetIdentityCheckArgs => raw as useGetIdentityCheckArgs,
  parseResponse: (raw: unknown): GetIdentityCheckRow => raw as GetIdentityCheckRow,
  specName: 'useGetIdentityCheck' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetIdentityCheckRow>;


// ─── useGetAuthProviders ────────────────────────────────────────────────────────────

export interface useGetAuthProvidersArgs { readonly fields?: readonly ('providers')[]; }


async function _useGetAuthProvidersQueryFn(
  args: useGetAuthProvidersArgs,
  ctx: QueryFunctionContext,
): Promise<GetAuthProvidersRow | null> {
  const url = '/v1/auth/providers';
  const params = new URLSearchParams();
  if (args.fields !== undefined) params.set('fields', args.fields.join(','));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetAuthProviders',
    routeTemplate: '/v1/auth/providers',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  // anyOf disambiguation: the data is one of N branches; the branch
  // containing the named field IS the row shape (do not unwrap further —
  // the row's own fields are the manifest's `fields:` list).
  return data as GetAuthProvidersRow;
}


const _useGetAuthProvidersFactory = Object.assign(
  (args: useGetAuthProvidersArgs) => ({
    queryKey: ['auth', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetAuthProvidersQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetAuthProviders',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['auth', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetAuthProvidersArgs, GetAuthProvidersRow>;


export const useGetAuthProvidersSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/auth/providers',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetAuthProvidersFactory },
  parseInput: (raw: unknown): useGetAuthProvidersArgs => raw as useGetAuthProvidersArgs,
  parseResponse: (raw: unknown): GetAuthProvidersRow => raw as GetAuthProvidersRow,
  specName: 'useGetAuthProviders' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetAuthProvidersRow>;
